/**
 * useContentStore — Zustand global store for the AI Creator workflow.
 *
 * All four workflow steps share a single source of truth here.
 * Both the left workspace and the right Agent panel read from / write to
 * this store, making cross-panel state control trivial.
 */
import { create } from "zustand";

/* ─── Shared types ────────────────────────────────────────────── */

export type WorkflowStage = "keyword" | "outline" | "writing" | "done";

export interface AgentMsg {
  id: string;
  role: "agent" | "user";
  content: string;
  /** When present, renders clickable title-suggestion cards */
  titles?: string[];
}

/* ─── State shape ─────────────────────────────────────────────── */

interface ContentState {
  /* Part 1 — Setup & Settings */
  keywords: string[];
  strategy: string;        // e.g. "edukasi"
  ojkStrict: boolean;
  selectedTitle: string;

  /* Part 2 — Content Outline */
  outline: string;
  outlineZH: string;
  outlineGenerated: boolean;

  /* Part 3 — Full Article */
  articleHtml: string;
  thinkingText: string;
  generationError: string;
  violations: string[];

  /* Part 4 — Social Media Snippets */
  bannerCopy: string;
  videoScriptCopy: string;

  /* Agent / Workflow */
  stage: WorkflowStage;
  agentMsgs: AgentMsg[];
  isAgentBusy: boolean;

  /* Loading flags */
  isGeneratingOutline: boolean;
  isGenerating: boolean;
  isGeneratingSocial: boolean;
}

/* ─── Actions ─────────────────────────────────────────────────── */

interface ContentActions {
  /* Part 1 */
  addKeyword: (kw: string) => void;
  removeKeyword: (kw: string) => void;
  setKeywords: (kws: string[]) => void;
  setStrategy: (s: string) => void;
  toggleOjk: () => void;
  setSelectedTitle: (t: string) => void;

  /* Part 2 */
  updateOutline: (text: string) => void;
  updateOutlineZH: (text: string) => void;
  setOutlineGenerated: (v: boolean) => void;

  /* Part 3 */
  updateArticle: (html: string) => void;
  updateThinking: (text: string) => void;
  setGenerationError: (msg: string) => void;
  setViolations: (v: string[]) => void;

  /* Part 4 */
  updateBannerCopy: (text: string) => void;
  updateVideoScript: (text: string) => void;

  /* Agent / Workflow */
  pushAgentMsg: (msg: Omit<AgentMsg, "id">) => void;
  setStage: (s: WorkflowStage) => void;
  setAgentBusy: (v: boolean) => void;

  /* Loading */
  setIsGeneratingOutline: (v: boolean) => void;
  setIsGenerating: (v: boolean) => void;
  setIsGeneratingSocial: (v: boolean) => void;

  /** Hard-reset to initial state (e.g. "New article") */
  reset: () => void;
}

/* ─── ID generator (stable across module reloads) ────────────── */

let _uid = 0;
function uid() {
  return `${Date.now()}_${++_uid}`;
}

/* ─── Initial state ───────────────────────────────────────────── */

const INITIAL_STATE: ContentState = {
  keywords: [],
  strategy: "edukasi",
  ojkStrict: true,
  selectedTitle: "",

  outline: "",
  outlineZH: "",
  outlineGenerated: false,

  articleHtml: "",
  thinkingText: "",
  generationError: "",
  violations: [],

  bannerCopy: "",
  videoScriptCopy: "",

  stage: "keyword",
  agentMsgs: [
    {
      id: "init",
      role: "agent",
      content:
        "👋 Hi! I'm your AI Editorial Agent. Click **[+ Add]** in the top bar to select keywords — I'll generate title suggestions to kick off your article.",
    },
  ],
  isAgentBusy: false,

  isGeneratingOutline: false,
  isGenerating: false,
  isGeneratingSocial: false,
};

/* ─── Store ───────────────────────────────────────────────────── */

export const useContentStore = create<ContentState & ContentActions>()(
  (set) => ({
    ...INITIAL_STATE,

    /* Part 1 */
    addKeyword: (kw) =>
      set((s) => ({
        keywords: s.keywords.includes(kw) ? s.keywords : [...s.keywords, kw],
      })),
    removeKeyword: (kw) =>
      set((s) => ({ keywords: s.keywords.filter((k) => k !== kw) })),
    setKeywords: (keywords) => set({ keywords }),
    setStrategy: (strategy) => set({ strategy }),
    toggleOjk: () => set((s) => ({ ojkStrict: !s.ojkStrict })),
    setSelectedTitle: (selectedTitle) => set({ selectedTitle }),

    /* Part 2 */
    updateOutline: (outline) => set({ outline }),
    updateOutlineZH: (outlineZH) => set({ outlineZH }),
    setOutlineGenerated: (outlineGenerated) => set({ outlineGenerated }),

    /* Part 3 */
    updateArticle: (articleHtml) => set({ articleHtml }),
    updateThinking: (thinkingText) => set({ thinkingText }),
    setGenerationError: (generationError) => set({ generationError }),
    setViolations: (violations) => set({ violations }),

    /* Part 4 */
    updateBannerCopy: (bannerCopy) => set({ bannerCopy }),
    updateVideoScript: (videoScriptCopy) => set({ videoScriptCopy }),

    /* Agent */
    pushAgentMsg: (msg) =>
      set((s) => ({
        agentMsgs: [...s.agentMsgs, { ...msg, id: uid() }],
      })),
    setStage: (stage) => set({ stage }),
    setAgentBusy: (isAgentBusy) => set({ isAgentBusy }),

    /* Loading */
    setIsGeneratingOutline: (isGeneratingOutline) =>
      set({ isGeneratingOutline }),
    setIsGenerating: (isGenerating) => set({ isGenerating }),
    setIsGeneratingSocial: (isGeneratingSocial) => set({ isGeneratingSocial }),

    reset: () => set(INITIAL_STATE),
  })
);
